"use client";

import React from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FlowButton } from "./flow-button";

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.43, 0.13, 0.23, 0.96],
      delayChildren: 0.1,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] },
  },
};

const numberVariants = {
  hidden: (direction: number) => ({
    opacity: 0,
    x: direction * 40,
    y: 15,
    rotate: direction * 5,
  }),
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    rotate: 0,
    transition: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] },
  },
};

const ghostVariants = {
  hidden: { scale: 0.8, opacity: 0, y: 15, rotate: -5 },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    rotate: 0,
    transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] },
  },
  hover: {
    scale: 1.1,
    y: -10,
    rotate: [0, -5, 5, -5, 0],
    transition: {
      duration: 0.8,
      ease: "easeInOut",
      rotate: {
        duration: 2,
        ease: "linear",
        repeat: Infinity,
        repeatType: "reverse",
      },
    },
  },
  floating: {
    y: [-5, 5],
    transition: {
      y: {
        duration: 2,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse",
      },
    },
  },
};

export function NotFoundContent() {
  const navigate = useNavigate();

  // Create motion values for smooth mouse tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Add spring physics for smoother follow effect
  const springX = useSpring(mouseX, { stiffness: 100, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 20 });

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Calculate mouse position relative to center of screen, normalize between -1 and 1
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;

      // Move by max 25px in any direction mapping from -1 to 1
      mouseX.set(x * 25);
      mouseY.set(y * 25);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <div className="flex flex-col items-center justify-center px-4 py-8 relative w-full h-full z-10">
      <AnimatePresence mode="wait">
        <motion.div
          className="text-center w-full max-w-[600px] mx-auto flex flex-col items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          {/* Error Icon Badge */}
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-border-subtle dark:border-white/10 w-fit shadow-sm mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            <span className="text-xs font-semibold text-text-sub dark:text-gray-300 uppercase tracking-wider">
              Error 404
            </span>
          </motion.div>

          <div className="flex items-center justify-center gap-4 md:gap-6 mb-8 md:mb-10 w-full relative">
            <motion.span
              className="text-[100px] md:text-[140px] font-bold text-text-main dark:text-white font-tight select-none leading-none relative z-10"
              variants={numberVariants}
              custom={-1}
            >
              4
            </motion.span>

            <motion.div
              variants={ghostVariants}
              whileHover="hover"
              animate={["visible", "floating"]}
              className="relative z-20"
              style={{
                x: springX,
                y: springY,
              }}
            >
              <img
                src="https://xubohuah.github.io/xubohua.top/Group.png"
                alt="Ghost"
                width={120}
                height={120}
                className="w-[90px] h-[90px] md:w-[130px] md:h-[130px] object-contain select-none drop-shadow-2xl"
                draggable="false"
              />
            </motion.div>

            <motion.span
              className="text-[100px] md:text-[140px] font-bold text-text-main dark:text-white font-tight select-none leading-none relative z-10"
              variants={numberVariants}
              custom={1}
            >
              4
            </motion.span>
          </div>

          <motion.h1
            className="font-tight text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-text-main dark:text-white mb-4 select-none"
            variants={itemVariants}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-600">
              Lost
            </span>{" "}
            in space
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-text-sub dark:text-gray-400 leading-relaxed max-w-[480px] mb-10 select-none"
            variants={itemVariants}
          >
            The page you are looking for might have been removed, had its name
            changed, or is temporarily unavailable.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full"
          >
            <FlowButton text="Go Back Home" onClick={() => navigate("/")} />
            <button
              onClick={() => navigate("/contact")}
              className="flex items-center justify-center gap-2 bg-transparent border border-border-subtle dark:border-white/20 text-text-main dark:text-white text-base font-semibold px-8 py-3 rounded-[100px] hover:bg-gray-50 dark:hover:bg-white/5 transition-all active:scale-[0.95]"
            >
              <span>Contact Support</span>
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
